import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";

import Bugsnag from "@bugsnag/js";
import { nanoid } from "nanoid";
import { consola } from "consola";
export const UserType = {
  client: "client",
  coach: "coach",
  admin: "admin",
};

export async function getConnectCodeUserCount({ connectCode }) {
  if (!connectCode) {
    consola.error(new Error("Incorrect parameters"));
  }

  if (window.location.hostname === "127.0.0.1") {
    return {
      status: "OK",
      count: 0,
    };
  }

  const response = await fetch(
    `https://app.mighty45.com/api/v1/connect_code_count/${connectCode}`,
    {
      method: "GET",
    },
  );
  if (response.status === 200) {
    const jsonData = await response.json();
    return jsonData;
  }
  consola.error(new Error("Error getting connect code user count"));
}

export async function setUserClaim({ userId, organizationId, authId }) {
  if (!userId || !organizationId || !authId) {
    consola.error(new Error("Incorrect parameters"));
  }

  const sendData = {
    userId: userId,
    organizationId: organizationId,
    authId: authId,
  };

  const json = JSON.stringify(sendData);

  const response = await fetch("https://app.mighty45.com/api/v1/claims", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: json,
  });
  if (response.status === 200) {
    const jsonData = await response.json();
    return jsonData;
  }
  consola.error(new Error("Error setting claim"));
}

/**
 * Check if the connect code is valid.
 * @param {*} param0
 * @returns
 */
export async function checkConnectCode({ connectCode }) {
  if (!connectCode) {
    consola.error(new Error("Incorrect parameters"));
  }

  const userRef = firebase
    .firestore()
    .collection("users")
    .where("connectCode", "==", connectCode);
  consola.debug("++++++ READ -> OrganizationData: checkConnectCode")
  const userData = await userRef.get();
  if (userData.docs.length > 0) {
    const doc = userData.docs[0];
    const userDataValues = doc.data();

    if (userDataValues.connectCode === connectCode) {
      if (userDataValues.type === UserType.coach) {
        return true;
      }
    }
  }

  return false;
}

async function removeLabelFromProtocolsHelper(organizationId, labelId) {
  const protocolRef = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("protocols")
    .where("protocolLabels", "array-contains-any", [labelId]);
  consola.debug("++++++ READ -> OrganizationData: removeLabelFromProtocols");
  const protocols = await protocolRef.get();
  const promises = protocols.docs.map((protocol) => {
    const data = protocol.data();
    if (data.protocolLabels) {
      const arr = [...data.protocolLabels];
      const ix = arr.indexOf(labelId);
      if (ix !== -1) {
        arr.splice(ix, 1);
        consola.debug("+++++ WRITE => OrganizationData: removeLabelFromProtocols");
        return protocol.ref.set(
          {
            protocolLabels: arr,
          },
          { merge: true },
        );
      }
    }
    return Promise.resolve();
  });
  await Promise.all(promises);
}

const OrganizationData = {
  getOrganization: async (uid) => {
    try {
      const userRef = firebase.firestore().collection("users").doc(uid);
      consola.debug("++++++ READ -> OrganizationData: getOrganization")
      const userData = await userRef.get();
      if (userData.exists) {
        const userDataValues = userData.data();

        const ref = firebase
          .firestore()
          .collection("organizations")
          .doc(userDataValues.organizationId);
        const data = await ref.get();

        return {
          ...data.data(),
          id: data.id,
        };
      }
    } catch (error) {
      Bugsnag.notify(error);
      throw error;
    }
  },
  getOrganizationById: async (id) => {
    try {
      const ref = firebase.firestore().collection("organizations").doc(id);
      consola.debug("++++++ READ -> OrganizationData: getOrganizationById")
      const data = await ref.get();

      return {
        ...data.data(),
        id: data.id,
      };
    } catch (error) {
      Bugsnag.notify(error);
      throw error;
    }
  },
  saveOrganizationData: async (uid, data) => {
    const userRef = firebase.firestore().collection("users").doc(uid);
    consola.debug("++++++ READ -> OrganizationData: saveOrganizationData")
    const userData = await userRef.get();
    if (userData.exists) {
      const userDataValues = userData.data();

      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(userDataValues.organizationId);

      if (
        userDataValues.role &&
        userDataValues.role === "admin" &&
        userDataValues.type &&
        userDataValues.type === UserType.coach
      ) {
        consola.info("+++++ WRITE => OrganizationData: saveOrganizationData")
        await ref.set(data, { merge: true });
        return true;
      }
    }
    return false;
  },
  /**
   * Autosave org/branding edits to a draft field (does not change live client-facing fields).
   */
  saveOrganizationDraft: async (uid, draftFields) => {
    const userRef = firebase.firestore().collection("users").doc(uid);
    consola.debug("++++++ READ -> OrganizationData: saveOrganizationDraft");
    const userData = await userRef.get();
    if (!userData.exists) return false;
    const userDataValues = userData.data();
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(userDataValues.organizationId);
    if (
      userDataValues.role === "admin" &&
      userDataValues.type === UserType.coach
    ) {
      consola.info("+++++ WRITE => OrganizationData: saveOrganizationDraft");
      await ref.set(
        {
          coachConsoleOrgDraft: {
            ...draftFields,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
        },
        { merge: true },
      );
      return true;
    }
    return false;
  },
  /**
   * Publish draft (or provided snapshot) to live organization fields and clear draft.
   */
  publishOrganizationSettings: async (uid, liveFields) => {
    const userRef = firebase.firestore().collection("users").doc(uid);
    consola.debug("++++++ READ -> OrganizationData: publishOrganizationSettings");
    const userData = await userRef.get();
    if (!userData.exists) return false;
    const userDataValues = userData.data();
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(userDataValues.organizationId);
    if (
      userDataValues.role === "admin" &&
      userDataValues.type === UserType.coach
    ) {
      consola.info("+++++ WRITE => OrganizationData: publishOrganizationSettings");
      await ref.set(
        {
          ...liveFields,
          coachConsoleOrgDraft: firebase.firestore.FieldValue.delete(),
          coachConsoleOrgPublishedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );
      return true;
    }
    return false;
  },
  /**
   * Autosave unified admin draft (General + questions + photos + labels snapshot).
   * Replaces coachConsoleAdminDraft in full — caller passes complete workingDraft object.
   */
  saveCoachConsoleAdminDraft: async (uid, draftFields) => {
    const userRef = firebase.firestore().collection("users").doc(uid);
    consola.debug("++++++ READ -> OrganizationData: saveCoachConsoleAdminDraft");
    const userData = await userRef.get();
    if (!userData.exists) return false;
    const userDataValues = userData.data();
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(userDataValues.organizationId);
    if (
      userDataValues.role === "admin" &&
      userDataValues.type === UserType.coach
    ) {
      consola.info("+++++ WRITE => OrganizationData: saveCoachConsoleAdminDraft");
      await ref.set(
        {
          coachConsoleAdminDraft: {
            ...draftFields,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
        },
        { merge: true },
      );
      return true;
    }
    return false;
  },
  /**
   * Discard saved admin draft only (does not change published org data).
   */
  clearCoachConsoleAdminDraft: async (uid) => {
    const userRef = firebase.firestore().collection("users").doc(uid);
    consola.debug("++++++ READ -> OrganizationData: clearCoachConsoleAdminDraft");
    const userData = await userRef.get();
    if (!userData.exists) return false;
    const userDataValues = userData.data();
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(userDataValues.organizationId);
    if (
      userDataValues.role !== "admin" ||
      userDataValues.type !== UserType.coach
    ) {
      return false;
    }
    try {
      consola.info("+++++ WRITE => OrganizationData: clearCoachConsoleAdminDraft");
      await ref.set(
        {
          coachConsoleAdminDraft: firebase.firestore.FieldValue.delete(),
          coachConsoleOrgDraft: firebase.firestore.FieldValue.delete(),
        },
        { merge: true },
      );
      return true;
    } catch (e) {
      Bugsnag.notify(e);
      consola.error(e);
      return false;
    }
  },
  removeLabelFromProtocols: removeLabelFromProtocolsHelper,
  /**
   * Publish in-memory admin draft to live org doc + labels subcollection, then clear drafts.
   * @param {object} draftPayload sparse draft from AdminDraftContext (same shape as coachConsoleAdminDraft minus updatedAt)
   */
  publishCoachConsoleAdminDraft: async (uid, draftPayload) => {
    const userRef = firebase.firestore().collection("users").doc(uid);
    consola.debug("++++++ READ -> OrganizationData: publishCoachConsoleAdminDraft");
    const userData = await userRef.get();
    if (!userData.exists) return false;
    const userDataValues = userData.data();
    if (
      userDataValues.role !== "admin" ||
      userDataValues.type !== UserType.coach
    ) {
      return false;
    }
    const orgId = userDataValues.organizationId;
    const orgRef = firebase.firestore().collection("organizations").doc(orgId);
    const d = draftPayload && typeof draftPayload === "object" ? draftPayload : {};

    try {
      const batch = firebase.firestore().batch();
      const orgUpdates = {
        coachConsoleAdminDraft: firebase.firestore.FieldValue.delete(),
        coachConsoleOrgDraft: firebase.firestore.FieldValue.delete(),
        coachConsoleOrgPublishedAt: firebase.firestore.FieldValue.serverTimestamp(),
      };
      if (d.organization && typeof d.organization === "object") {
        Object.assign(orgUpdates, d.organization);
        if (Object.prototype.hasOwnProperty.call(d.organization, "name")) {
          const n = d.organization.name;
          orgUpdates.nameLower =
            typeof n === "string" ? n.trim().toLowerCase() : "";
        }
      }
      if (d.weeklyOptional !== undefined) {
        orgUpdates.weeklyOptional = d.weeklyOptional;
      }
      if (d.optional !== undefined) {
        orgUpdates.optional = d.optional;
      }
      if (d.progressPhotoConfiguration !== undefined) {
        orgUpdates.progressPhotoConfiguration = d.progressPhotoConfiguration;
      }
      batch.set(orgRef, orgUpdates, { merge: true });

      if (d.labels !== undefined && Array.isArray(d.labels)) {
        const labelsCol = orgRef.collection("labels");
        const existingSnap = await labelsCol.get();
        const existingIds = new Set(existingSnap.docs.map((x) => x.id));
        const draftIds = new Set(d.labels.map((l) => l.id).filter(Boolean));
        for (const id of existingIds) {
          if (!draftIds.has(id)) {
            await removeLabelFromProtocolsHelper(orgId, id);
          }
        }
        existingIds.forEach((id) => {
          if (!draftIds.has(id)) {
            batch.delete(labelsCol.doc(id));
          }
        });
        d.labels.forEach((label) => {
          if (!label || !label.id) return;
          batch.set(labelsCol.doc(label.id), label, { merge: true });
        });
      }

      await batch.commit();
      consola.info("+++++ WRITE => OrganizationData: publishCoachConsoleAdminDraft committed");
      return true;
    } catch (e) {
      Bugsnag.notify(e);
      consola.error(e);
      return false;
    }
  },
  getCoaches: async (organizationId) => {
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("coaches");
      
    consola.info("++++++ READ -> OrganizationData getCoaches")
    const coaches = await ref.get();
    const promises = coaches.docs.map((d) => {
      return new Promise((resolve, reject) => {
        const data = d.data();

        const userRef = firebase.firestore().collection("users").doc(data.uid);
        userRef
          .get()
          .then((user) => {
            if (user.exists) {
              resolve({
                ...user.data(),
              });
            } else {
              reject(new Error(`User does not exist (${data.uid})`));
            }
          })
          .catch((e) => {
            reject(e);
          });
      });
    });
    const final = await Promise.all(promises);

    return final;
  },
  usersWithConnectCode: async (connectCode) => {
    const userRef = firebase
      .firestore()
      .collection("users")
      .where("connectCode", "==", connectCode);
    consola.debug("++++++ READ -> OrganizationData: usersWithConnectCode")
    const userData = await userRef.get();
    return userData.docs;
  },
  addToOrganization: async (authId, connectCode) => {
    const userRef = firebase
      .firestore()
      .collection("users")
      .where("connectCode", "==", connectCode);
    consola.debug("++++++ READ -> OrganizationData: addToOrganization")
    const userData = await userRef.get();
    if (userData.docs.length > 0) {
      const doc = userData.docs[0];
      const userDataValues = userData.docs[0].data();

      if (
        userDataValues.connectCode === connectCode &&
        userDataValues.type === UserType.coach
      ) {
        if (!userDataValues.authId) {
          // Retrieve new token.
          await firebase.auth().currentUser.getIdTokenResult(true);
          consola.info("+++++ WRITE => OrganizationData: addToOrganization")
          await doc.ref.set(
            {
              authId: authId,
              type: UserType.coach,
              role: UserType.coach,
              onboardedOn: new Date(),
            },
            { merge: true },
          );
          return userDataValues;
        }
      }
    }
    return null;
  },
  addOrganization: async (userId, input) => {
    const userRef = firebase.firestore().collection("users").doc(userId);
    consola.debug("++++++ READ -> OrganizationData: addOrganization")
    const userData = await userRef.get();
    if (userData.exists) {
      const organizationId = nanoid();

      // Retrieve new token.
      await firebase.auth().currentUser.getIdTokenResult(true);

      const organizationRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId);

      const coachRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("coaches")
        .doc(userId);

      try {
        await firebase.firestore().runTransaction(async () => {
          await organizationRef.set(input, { merge: true });
          await userRef.set(
            {
              organizationId: organizationId,
              updatedOn: new Date(),
            },
            { merge: true },
          );
          await coachRef.set(
            {
              uid: userId,
              status: "active",
              connectCode: "",
              updatedAt: new Date(),
            },
            { merge: true },
          );
        });
        return organizationId;
      } catch (e) {
        consola.log(e);
        Bugsnag.notify(e);
        return null;
      }
    }
    return null;
  },
};

export default OrganizationData;
